import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    ChevronDownIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import {
    sidebarMenus,
    filterableSidebarMenus,
} from "../../../router/routes/Auth";
import IconInput from "../../new/inputs/icon-input";
import { useDispatch, useSelector } from "react-redux";
import { confirmDialog } from "primereact/confirmdialog";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { handleLogout } from "../../../store/authentication";
import { Skeleton } from "primereact/skeleton";
import FallbackImage from "../../new/fallback-image";
import {
    getSettingsStorage,
    settingsHandler,
} from "../../../utility/settings.utils";
import { getFileUrl } from "../../../utility/helpers";
import { ROLE_TYPES } from "../../../utility/constants";

const nestedMenuPaddingsByLevel = {
    0: "",
    1: "pl-6",
    2: "pl-11",
};

export default function Sidebar({
    isVisible,
    isHover,
    setIsHover,
    handleSidebarCallback,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const divRef = useRef(null);
    const buttonRef = useRef(null);
    const [isVisibleMenu, setIsVisibleMenu] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filteredMenu, setFilteredMenu] = useState([]);
    const [toggelUserMenu, setToggelUserMenu] = useState(false);
    const sidebarRef = useRef(null);
    const location = useLocation();
    const userState = useSelector((state) => state.auth);
    const currentUser = userState.userData?.user;
    const [logo, setLogo] = useState("");
    const [loading, setLoading] = useState(false);

    const menus = sidebarMenus;

    const handleClickOutside = (event) => {
        if (buttonRef.current && !buttonRef.current.contains(event.target)) {
            if (divRef.current && !divRef.current.contains(event.target)) {
                setIsVisibleMenu(false);
            }
        }
    };
    useEffect(async () => {
        if (!localStorage.getItem("settings")) await settingsHandler();
        const companyInfo = getSettingsStorage("company_info");
        if (companyInfo?.logo) setLogo(companyInfo?.logo);
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        // on page load we have to expand the nested menu header
        if (sidebarRef.current) {
            collapseOpenSubMenu();
            expandSelectedMenu(location.pathname);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (searchText) {
            const menus = filterableSidebarMenus.filter((menu) => {
                const path = menu.displayName || menu.fullPath || menu.path;
                if (path?.toLowerCase()?.includes(searchText.toLowerCase()))
                    return menu;
            });
            setFilteredMenu(menus);
        }
    }, [searchText]);

    const signOut = async (event) => {
        dispatch(handleLogout());
        navigate("/login");
        // LATER: add a loader
        // await logout()
        //     .then((response) => {
        //         dispatch(handleLogout());
        //         navigate("/login");
        //     })
        //     .catch((error) => {
        //         console.log("error..", error);
        //     });
    };

    const onSearchMenuClick = (path) => {
        collapseOpenSubMenu();
        navigate(path);
        if (path.match("/")) expandSelectedMenu(`/${path}`);
        setSearchText("");
        setFilteredMenu([]);
    };

    const openSignOutDialog = () => {
        if (!isVisible) setToggelUserMenu(false);
        confirmDialog({
            message: (
                <div className="flex items-center gap-x-1">
                    <InformationCircleIcon className="w-6 h-6" /> Are you sure
                    you want to sign out?{" "}
                </div>
            ),
            header: "Sign out Confirmation",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            accept: signOut,
            reject: () => setToggelUserMenu(false),
        });
    };

    const expandSelectedMenu = (currentPath) => {
        const splitPath = currentPath.split("/");
        splitPath.splice(0, 1); // remove the blank element from array

        if (splitPath.length > 1) {
            splitPath.pop(); // remove the last path from array. Because it is a single menu.
            let lastPath = "";
            splitPath.forEach((path) => {
                try {
                    const id = `#${
                        lastPath === "" ? path : `${lastPath}_${path}`
                    }`;
                    const submenuContainer =
                        sidebarRef.current.querySelector(id);
                    if (submenuContainer) {
                        const ulElement = submenuContainer.querySelector("ul");
                        ulElement.classList.add("open-submenu");
                        ulElement.classList.remove("hidden");

                        // rotate down arrow icon to up.
                        submenuContainer
                            ?.querySelector(".nested-menu-icon")
                            ?.classList.add("-rotate-180");
                    }
                } catch (e) {
                    console.log("selector is not valid");
                }
                lastPath = path;
            });
        }
    };

    const handleNestedMenuClick = (e, path) => {
        e.preventDefault();
        e.stopPropagation();

        let parentElementId = null;

        if (currentUser?.role_type === ROLE_TYPES.GDS_CALL_CENTRE_AGENT) {
            parentElementId = path;
        } else {
            parentElementId = path.split("_")[0];
        }

        // collapse all expanded sub menu
        collapseOpenSubMenu(parentElementId);

        const btnElement = document.querySelector(`#${path}`);
        const ulElement = btnElement.querySelector("ul");
        btnElement
            .querySelector(".nested-menu-icon")
            .classList.toggle("-rotate-180");

        // add open-submenu class to track for hide on another sub menu expand.
        ulElement.classList.add("open-submenu");

        // toggle hidden class
        ulElement.classList.toggle("hidden");
    };

    const collapseOpenSubMenu = (parentElementId = null) => {
        document
            .querySelector("#sidebar-menus")
            .querySelectorAll(".open-submenu")
            .forEach((element) => {
                if (
                    !parentElementId ||
                    !document
                        .querySelector(`#${parentElementId}`)
                        ?.contains(element)
                ) {
                    element.classList.remove("open-submenu");
                    element.classList.add("hidden");
                    element.previousElementSibling
                        ?.querySelector(".nested-menu-icon")
                        .classList.remove("-rotate-180");
                }
            });
    };

    const singleMenu = (menu) => {
        const nestedMenuClass = getNestedMenuClass(
            (isVisible || isHover) && menu.fullPath,
            menu
        );

        return (
            <NavLink
                to={menu.fullPath ? menu.fullPath : menu.path}
                className={({ isActive }) =>
                    `flex items-center p-2 transition duration-75 dark:bg-white rounded-lg hover:text-red-500 hover:bg-white group ${
                        isActive ? "bg-white text-red-500" : "text-[#D6D3D1]"
                    }${nestedMenuClass}`
                }
                onClick={(e) => {
                    e.stopPropagation();
                    if (!menu.fullPath) collapseOpenSubMenu();
                }}
            >
                <menu.icon className="w-6 h-6 flex-shrink-0" />
                <span
                    className={`ms-3 ${!isVisible && !isHover ? "hidden" : ""}`}
                >
                    {menu.displayName}
                </span>
            </NavLink>
        );
    };

    const nestedMenu = (menu) => {
        const nestedMenuClass = getNestedMenuClass(
            (isVisible || isHover) && menu.fullPath,
            menu
        );

        const path = (menu.fullPath || menu.path).replaceAll("/", "_");

        return (
            <div onClick={(e) => handleNestedMenuClick(e, path)} id={path}>
                <button
                    type="button"
                    className={`flex items-center w-full p-2 text-base dark:bg-white transition duration-75 rounded-lg
                    group hover:text-red-600 hover:bg-white text-[#D6D3D1]${nestedMenuClass}`}
                >
                    <menu.icon className="w-6 h-6 flex-shrink-0" />

                    <>
                        <span
                            className={`${
                                !isVisible && !isHover ? "hidden" : ""
                            }  hover:flex flex-1 ms-3 text-left rtl:text-right whitespace-nowrap`}
                        >
                            {menu.displayName}
                        </span>
                        <ChevronDownIcon
                            className={`w-5 h-5 hover:block flex-shrink-0 nested-menu-icon ${
                                !isVisible && !isHover ? "hidden" : ""
                            }`}
                        />
                    </>
                </button>
                <ul
                    className={`py-2 space-y-2 transition duration-200 ease-in-out delay-500 hidden`}
                >
                    {menu.children.map((submenu, submenuIndex) => (
                        <li key={`submenu-${submenuIndex + 1}`}>
                            {submenu.showChildren && submenu.children?.length
                                ? nestedMenu(submenu)
                                : singleMenu(submenu)}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <>
            <div className="md:hidden flex justify-between px-4">
                <button
                    type="button"
                    onClick={handleSidebarCallback}
                    className="inline-flex items-center p-2 text-sm text-white rounded-lg hover:bg-white hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                    <span className="sr-only">Open sidebar</span>
                    <svg
                        className="w-6 h-6"
                        aria-hidden="true"
                        fill="#000"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            clipRule="evenodd"
                            fillRule="evenodd"
                            d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                        ></path>
                    </svg>
                </button>
                {isVisible && (
                    <button
                        onClick={handleSidebarCallback}
                        className="outline-none"
                    >
                        <XMarkIcon className="w-6 h-6 text-black" />
                    </button>
                )}
            </div>
            <aside
                id="sidebar-menus"
                className={`fixed top-0 left-0 z-50 ${
                    isVisible ? "w-64" : "w-0 sm:w-16 hidden sm:block"
                } hover:w-64 h-screen transition-transform duration-300 ease-in-out dark:bg-gray-900 border-r border-gray-200 z-[9999]`}
                aria-label="Sidebar"
                onMouseOver={() => setIsHover(true)}
                onMouseLeave={() => {
                    if (!isVisible) {
                        setToggelUserMenu(false);
                        setIsVisibleMenu(false);
                    }
                    setIsHover(false);
                }}
                ref={sidebarRef}
            >
                <div className="flex flex-col gap-y-3 h-full p-3 overflow-y-auto dark:bg-gray-900 bg-[#1C1917] no-scrollbar">
                    <ul>
                        <li className="flex">
                            <button
                                type="button"
                                onClick={() => {
                                    handleSidebarCallback();
                                    setToggelUserMenu(false);
                                }}
                                className="inline-flex items-center p-2 text-sm text-white rounded-lg hover:bg-white hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-gray-200"
                            >
                                <span className="sr-only">Open sidebar</span>
                                <svg
                                    className="w-6 h-6"
                                    aria-hidden="true"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        clipRule="evenodd"
                                        fillRule="evenodd"
                                        d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                                    ></path>
                                </svg>
                            </button>
                            {isVisible || isHover ? (
                                <>
                                    <a
                                        href="/"
                                        id="bdtickets-logo"
                                        className="inline-flex items-center ms-2"
                                    >
                                        {loading ? (
                                            <Skeleton
                                                width="92px"
                                                borderRadius="4px"
                                                className="!h-8 !me-3"
                                            />
                                        ) : (
                                            <FallbackImage
                                                src={getFileUrl(logo)}
                                                fallbackSrc={`/images/logo.png`}
                                                alt={"bdtickets Logo"}
                                                className="h-8 me-3 w-[95px]"
                                            />
                                        )}
                                    </a>
                                    <div className="flex items-center ml-auto">
                                        <div>
                                            <button
                                                ref={buttonRef}
                                                type="button"
                                                className="flex text-sm bg-white rounded-full focus:ring-4 focus:ring-gray-300"
                                                aria-expanded="false"
                                                data-dropdown-toggle="dropdown-user"
                                                onClick={() =>
                                                    setIsVisibleMenu(
                                                        (prev) => !prev
                                                    )
                                                }
                                                // onClick={() =>
                                                //     setToggelUserMenu(
                                                //         !toggelUserMenu
                                                //     )
                                                // }
                                            >
                                                <span className="sr-only">
                                                    Open user menu
                                                </span>
                                                <div className="flex justify-center items-center w-8 h-8 rounded-full text-grey-800">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 24 24"
                                                        fill="currentColor"
                                                        className="w-6 h-6"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : null}
                        </li>
                    </ul>

                    <div
                        ref={divRef}
                        className={`text-base list-none bg-white divide-y divide-gray-100 rounded ${
                            isVisibleMenu ? "block" : "hidden"
                        }`}
                        id="dropdown-user"
                    >
                        <div className="px-4 py-3" role="none">
                            <p
                                className="text-sm text-gray-900 font-semibold"
                                role="none"
                            >
                                {currentUser && currentUser.name
                                    ? currentUser?.name
                                    : currentUser?.username || ""}
                            </p>
                        </div>
                        <ul className="py-1" role="none">
                            <li>
                                <Link
                                    to={"/profile"}
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    Profile
                                </Link>
                            </li>
                            <li>
                                <a
                                    href="#"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                    onClick={openSignOutDialog}
                                >
                                    Sign out
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className="relative">
                        {isVisible || isHover ? (
                            <IconInput
                                Icon={MagnifyingGlassIcon}
                                type="text"
                                placeholder="Search Menu"
                                className="w-full p-1.5 rounded-md focus:outline-none focus:border-red-400"
                                value={searchText}
                                onChange={({ target }) =>
                                    setSearchText(target.value)
                                }
                            />
                        ) : (
                            <div
                                className={`flex items-center p-2 transition duration-75 dark:bg-white rounded-lg hover:text-red-500 hover:bg-white group ${
                                    isVisible
                                        ? "bg-white text-red-500"
                                        : "text-[#D6D3D1]"
                                }`}
                            >
                                <MagnifyingGlassIcon className="w-6 h-6 flex-shrink-0" />
                            </div>
                        )}
                        <div
                            className={`${
                                !searchText || !filteredMenu.length
                                    ? "hidden"
                                    : "block"
                            } absolute w-full max-h-80 bg-white -mt-[3px] rounded-md overflow-y-auto border sidebar-filtered-menu`}
                        >
                            <div className="flex flex-col gap-y-1 w-full">
                                {filteredMenu.map((menu, index) => (
                                    <div
                                        key={index}
                                        className={`group p-1.5 w-full cursor-pointer hover:bg-white${
                                            index < filteredMenu.length - 1
                                                ? " border-b border-b-gray-300"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            onSearchMenuClick(
                                                menu.fullPath || menu.path
                                            )
                                        }
                                    >
                                        <p className="text-sm font-medium break-all text-gray-600 group-hover:text-red-500">
                                            {menu.displayPath ||
                                                menu.displayName}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <ul className="space-y-2 font-medium">
                        {menus.map((menu, index) => (
                            <li key={`menu-${index + 1}`}>
                                {menu.showChildren && menu.children?.length
                                    ? nestedMenu(menu)
                                    : singleMenu(menu)}
                            </li>
                        ))}
                    </ul>
                </div>
            </aside>
        </>
    );
}

const getNestedMenuClass = (canApply, { path, fullPath }) => {
    if (!canApply) return "";
    const splitPath = fullPath.split("/");
    const index = splitPath.indexOf(path);
    return ` transition duration-75 ${nestedMenuPaddingsByLevel[index]}`;
};
